"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronUp, FiCopy, FiRefreshCw, FiThumbsDown, FiThumbsUp, FiX } from "react-icons/fi";
import { Button, Tooltip } from "@/components/ui";
import AIBadge from "@/components/ui/ai-badge";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUser } from "@/contexts/user-provider";
import { useEventTitle } from "@/hooks/use-event-title";
import { usePatientTabNavigation } from "@/hooks/use-patient-tab-navigation";
import { parseAISummaryResponse, stripCitationRefs } from "@/lib/ai/parse-response";
import { renderTextWithCitations as renderTextWithCitationsUtil } from "@/lib/format-ai-text";
import { formatDateTime } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { SSEStreamError } from "@/lib/stream-sse";
import { cx } from "@/lib/tailwind-utils";
import { toMs } from "@/lib/timestamp";
import type { PatientAccountSummary, PatientSummaryContext } from "@/types/ai";
import type { ChargeTableItem } from "@/types/charge";
import type { DocumentSummary } from "@/types/document";
import { AIFeedbackType } from "@/types/enums";
import { EventStatus } from "@/types/enums/event";
import { MemberStatus } from "@/types/enums/membership";
import type { EventTiny } from "@/types/event";
import type { FormTable, PatientSubmissionTableItem, PatientSubmissionsTable } from "@/types/forms/form";
import type { NoteListItem } from "@/types/notes";
import type { PatientDetail } from "@/types/patient";
import type { QuickNoteSummary } from "@/types/quick-note";
import { AIQAInterface } from "./ai-qa-interface";
import { streamPatientSummary } from "@/api/ai/patient";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
import { deletePatientAISummary, savePatientAISummary, submitAISummaryFeedback } from "@/api/provider/user/patient/ai-summary";
import { aiSummaryQueryKeys, aiSummaryQueryOptions } from "@/api/provider/user/patient/ai-summary-query";
import { getPatientDocuments } from "@/api/provider/user/patient/document";
import { documentQueryKeys } from "@/api/provider/user/patient/document-query";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";

/**
 * Computes the most recent creation/update date from patient data sources
 * Used to detect if the AI summary is stale (data has changed since generation)
 * Uses createdDate and updatedDate to detect when records were added or modified
 */
function getLatestDataDate(patient: PatientDetail, recentNotes?: NoteListItem[], appointments?: EventTiny[], charges?: ChargeTableItem[], formSubmissions?: PatientSubmissionsTable, quickNotes?: QuickNoteSummary[], documents?: DocumentSummary[]): Date | null {
  const times: number[] = [];

  // Helper to safely add a date
  const addDate = (date: Date | string | null | undefined) => {
    if (date) times.push(toMs(date));
  };

  // Measurements dates (only createdDate available)
  patient.measurements?.forEach(m => {
    addDate(m.createdDate);
  });

  // Medications dates (only createdDate available on MedicationSummary)
  patient.medications?.forEach(med => {
    addDate(med.createdDate);
  });

  // Notes dates (uses lastEditedDate for updates)
  recentNotes?.forEach(note => {
    addDate(note.createdDate);
    addDate(note.lastEditedDate);
  });

  // Appointments dates - use createdDate and updatedDate (not start which is when apt occurs)
  appointments?.forEach(apt => {
    addDate(apt.createdDate);
    addDate(apt.updatedDate);
  });

  // Charges dates (only createdDate available on ChargeTableItem)
  charges?.forEach(charge => {
    addDate(charge.createdDate);
  });

  // Form submissions dates (only submissionDate available)
  formSubmissions?.data?.forEach(submission => {
    addDate(submission.submissionDate);
  });

  // Quick notes dates
  quickNotes?.forEach(note => {
    addDate(note.createdDate);
    addDate(note.updatedDate);
  });

  // Document dates: updatedDate catches title/type edits (rename/retype keeps
  // the original createdDate), so a cached summary refreshes when metadata
  // shown in the prompt changes.
  documents?.forEach(doc => {
    addDate(doc.createdDate);
    addDate(doc.updatedDate);
  });
  if (times.length === 0) return null;

  // Return the most recent date
  return new Date(Math.max(...times));
}
interface AISummarySectionProps {
  patient: PatientDetail;
  charges?: ChargeTableItem[];
  forms?: FormTable[];
  formSubmissions?: PatientSubmissionsTable;
  quickNotes?: QuickNoteSummary[];
}

/**
 * Cap on how many recent documents are fetched and fed into the summary prompt,
 * to bound query/serialization cost and prompt size.
 */
const MAX_SUMMARY_DOCUMENTS = 25;

/**
 * Tab navigation mapping for citations
 */
const TAB_MAP: Record<string, string> = {
  demographics: "overview",
  appointments: "appointments",
  "medical-notes": "medical-notes",
  measurements: "overview",
  medications: "overview",
  billing: "billing",
  "forms-documents": "forms-documents"
};

/**
 * Builds the patient context for AI prompts from available data
 */
function buildContextFromPatient(getEventTitle: (event: EventTiny) => string, patient: PatientDetail, recentNotes?: NoteListItem[], appointments?: EventTiny[], charges?: ChargeTableItem[], forms?: FormTable[], formSubmissions?: PatientSubmissionsTable, quickNotes?: QuickNoteSummary[], documents?: DocumentSummary[]): PatientSummaryContext {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  // Separate upcoming vs recent appointments
  const upcomingAppointments: EventTiny[] = [];
  const recentAppointments: EventTiny[] = [];
  if (appointments) {
    appointments.forEach(apt => {
      const aptMs = toMs(apt.start);

      // Non-CUSTOM appointments persist a NULL title and render dynamically
      // from patient + service(s); resolve it the same way the calendar/UI does
      // so the summary gets the real title instead of a blank/"None".
      const resolved = {
        ...apt,
        title: getEventTitle(apt)
      };
      if (aptMs >= now.getTime()) {
        upcomingAppointments.push(resolved);
      } else if (aptMs >= thirtyDaysAgo.getTime()) {
        recentAppointments.push(resolved);
      }
    });

    // Sort upcoming by date ascending, recent by date descending
    upcomingAppointments.sort((a, b) => toMs(a.start) - toMs(b.start));
    recentAppointments.sort((a, b) => toMs(b.start) - toMs(a.start));
  }

  // Process charges to extract recent charges
  const recentCharges: ChargeTableItem[] = [];
  if (charges && Array.isArray(charges) && charges.length > 0) {
    charges.filter((charge: ChargeTableItem) => {
      if (!charge.createdDate) return false;
      return toMs(charge.createdDate) >= thirtyDaysAgo.getTime();
    }).sort((a, b) => {
      // Sort by createdDate descending (most recent first)
      return toMs(b.createdDate) - toMs(a.createdDate);
    }).slice(0, 5).forEach((charge: ChargeTableItem) => {
      recentCharges.push(charge);
    });
  }

  // Process form submissions - map to include form name for backend schema
  const recentForms: Array<PatientSubmissionTableItem & {
    name: string;
  }> | undefined = (() => {
    if (!formSubmissions?.data || !forms) return undefined;
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const recentFormSubmissions = formSubmissions.data.filter(submission => {
      return toMs(submission.submissionDate) >= thirtyDaysAgo.getTime();
    }).sort((a, b) => {
      // Sort by submissionDate descending (most recent first)
      return toMs(b.submissionDate) - toMs(a.submissionDate);
    }).slice(0, 5).map(submission => {
      // Find the form name from the forms array
      const form = forms.find(f => f.id === submission.formId);
      return {
        ...submission,
        name: form?.name || "Unknown Form"
      };
    });
    return recentFormSubmissions.length > 0 ? recentFormSubmissions : undefined;
  })();

  // Process notes
  const processedNotes = recentNotes?.slice().sort((a, b) => {
    // Sort by createdDate descending (most recent first)
    return toMs(b.createdDate) - toMs(a.createdDate);
  });

  // Process quick notes
  const processedQuickNotes = quickNotes?.slice().sort((a, b) => {
    // Sort by createdDate descending (most recent first)
    return toMs(b.createdDate) - toMs(a.createdDate);
  });

  // Process documents - only name + upload date are sent to the AI (no contents).
  // Sorted most-recent-first and capped to keep the prompt bounded.
  const processedDocuments = documents?.slice().sort((a, b) => {
    const timeA = a.createdDate ? toMs(a.createdDate) : 0;
    const timeB = b.createdDate ? toMs(b.createdDate) : 0;
    const dateA = Number.isNaN(timeA) ? 0 : timeA;
    const dateB = Number.isNaN(timeB) ? 0 : timeB;
    return dateB - dateA;
  }).slice(0, MAX_SUMMARY_DOCUMENTS);

  // Lifetime/account aggregates: anchor the summary for established patients
  // whose recent windows are empty. totalSpent/appointmentCount/creditBalance
  // are lifetime figures already on the chart; lastAppointmentDate is the most
  // recent COMPLETED visit we have (best-effort "last seen") — cancelled/no-show
  // events are not a visit and must not count.
  const lastCompletedVisit = (appointments ?? []).filter(apt => apt.status === EventStatus.COMPLETED && toMs(apt.start) < now.getTime()).reduce<EventTiny | undefined>((latest, apt) => !latest || toMs(apt.start) > toMs(latest.start) ? apt : latest, undefined);
  const activeMembershipNames = (patient.memberships ?? []).filter(m => m.status === MemberStatus.ACTIVE).map(m => m.membership.name);
  const account: PatientAccountSummary = {
    totalSpent: patient.totalSpent ?? undefined,
    appointmentCount: patient.appointmentCount ?? undefined,
    creditBalance: patient.creditBalance ?? undefined,
    lastAppointmentDate: lastCompletedVisit?.start,
    memberships: activeMembershipNames.length > 0 ? activeMembershipNames : undefined
  };
  const hasAccountData = Object.values(account).some(v => v !== undefined);
  return {
    patient,
    account: hasAccountData ? account : undefined,
    upcomingAppointments: upcomingAppointments.length > 0 ? upcomingAppointments : undefined,
    recentAppointments: recentAppointments.length > 0 ? recentAppointments : undefined,
    recentNotes: processedNotes,
    measurements: patient.measurements || undefined,
    // Ensure medications are included with all fields (amount, unit, frequency, etc.)
    medications: patient.medications && patient.medications.length > 0 ? patient.medications : undefined,
    recentCharges: recentCharges.length > 0 ? recentCharges : undefined,
    recentForms,
    quickNotes: processedQuickNotes,
    // Preserve the empty-vs-absent distinction: [] means "loaded, none on
    // file" (the prompt says so); undefined means "not loaded / no permission"
    // (the prompt stays silent rather than asserting the patient has none).
    documents: processedDocuments
  };
}
export default function AISummarySection(props) {
  const $ = _c(2);
  const {
    currentUser
  } = useUser();
  if (!hasPermission(currentUser, PERMISSIONS.AI_USE)) {
    return null;
  }
  let t0;
  if ($[0] !== props) {
    t0 = <AISummarySectionContent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function AISummarySectionContent({
  patient,
  charges,
  forms,
  formSubmissions,
  quickNotes
}: AISummarySectionProps) {
  const queryClient = useQueryClient();
  const {
    currentUser
  } = useUser();

  // Load uploaded document metadata (name + date) so the summary can reference
  // documents on file. Self-contained here because the AI summary lives on the
  // overview tab, where the page-level documents query is not enabled.
  const canViewDocuments = hasPermission(currentUser, PERMISSIONS.PATIENTS_DOCUMENTS_READ);
  // Fetch documents for the summary. `requestData` swallows GET failures to
  // null, so throw on null to let React Query treat it as an error (and retry
  // transient failures) instead of a successful empty result. A distinct query
  // key avoids colliding with the unbounded forms-documents-tab cache + select.
  // The list is bounded to the most-recent MAX_SUMMARY_DOCUMENTS client-side.
  const {
    data: queriedDocuments,
    isSuccess: documentsSucceeded,
    isError: documentsErrored
  } = useQuery({
    queryKey: [...documentQueryKeys.list(patient.id), "ai-summary"],
    queryFn: async () => {
      const page = await getPatientDocuments(patient.id);
      if (page == null) {
        throw new Error("Failed to load patient documents");
      }
      return page.data;
    },
    enabled: canViewDocuments
  });
  // Only trust a successful fetch; otherwise leave documents undefined so the
  // prompt omits them rather than asserting none on file. Also honors the
  // permission gate even if a prior fetch is cached (access revoked mid-session).
  const documents = canViewDocuments && documentsSucceeded ? queriedDocuments : undefined;
  // Generation reads `documents` and the result is cached per patient, so wait
  // until documents settle: ready when the viewer can't see documents, the
  // fetch succeeded, or it failed terminally after retries — in which case we
  // degrade gracefully and generate without documents rather than blocking the
  // summary indefinitely on a transient documents outage. Gates the auto-
  // generate effect AND the manual regenerate / Q&A entry points below.
  const documentsReady = !canViewDocuments || documentsSucceeded || documentsErrored;
  // ...but only PERSIST a generated summary when the document context is
  // trustworthy (viewer can't see documents, or the fetch succeeded). On a
  // terminal documents failure we still show a live summary but don't cache a
  // doc-less one — the staleness check compares timestamps and can't later
  // detect that documents were missing, so a cached doc-less summary would
  // otherwise survive indefinitely and be served to other users.
  const documentsTrusted = !canViewDocuments || documentsSucceeded;

  // Appointments get the same wait-then-trust treatment, read from the cache
  // the chart's appointment list shares. A summary generated before they load
  // says "no upcoming appointments", and nothing later marks it stale — the
  // appointment's own dates predate the summary. `requestData` resolves a
  // failed GET to null: settled, but not trustworthy.
  const {
    data: queriedAppointments,
    isPending: appointmentsPending
  } = useQuery(eventQueryOptions.searchPatientEvents(patient.id, ""));
  const appointments = queriedAppointments ?? undefined;

  // Notes get the same treatment, from the cache the chart's notes list shares.
  // The summary is cached per patient and served to every viewer, so one built
  // without notes, because they were still loading or this viewer can't read
  // them, would tell the next provider "no notes on file".
  const canViewNotes = hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ);
  const {
    data: notesPage,
    isPending: notesPending
  } = useQuery({
    ...noteQueryOptions.list({
      patientId: patient.id,
      sortBy: "visit_date",
      sortDirection: "desc"
    }),
    enabled: canViewNotes
  });
  const recentNotes = canViewNotes ? notesPage?.data : undefined;
  const contextReady = documentsReady && !appointmentsPending && (!canViewNotes || !notesPending);
  const canPersistSummary = documentsTrusted && appointments !== undefined && recentNotes !== undefined;
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoverExpanded, setHoverExpanded] = useState(true);
  const [showQA, setShowQA] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [pendingFeedbackType, setPendingFeedbackType] = useState<AIFeedbackType | null>(null);
  const {
    navigateToTab,
    highlightSection
  } = usePatientTabNavigation();

  // Filter out format markers during streaming (handles partial markers too)
  const filterFormatMarkers = (text: string): string => {
    if (!text) return text;
    // Remove complete markers and partial markers that might appear during streaming
    return text.replace(/---SUMMARY---/gi, "").replace(/---SUGGESTIONS---/gi, "").replace(/---SUMMARY/gi, "").replace(/---SUGGESTIONS/gi, "").replace(/SUMMARY---/gi, "").replace(/SUGGESTIONS---/gi, "").replace(/^---+/gm, "") // Remove any remaining lines that start with ---
    .trim();
  };

  // Extract summary text from partial JSON during streaming
  // Filters out JSON structure characters and extracts just the summary content
  const extractSummaryFromPartialJSON = (text_0: string): string => {
    if (!text_0) return text_0;

    // Try to parse as complete JSON first
    try {
      const parsed = JSON.parse(text_0.trim());
      if (parsed && typeof parsed === "object" && "summary" in parsed) {
        return String(parsed.summary || "").trim();
      }
    } catch {
      // Not complete JSON yet, try to extract summary field from partial JSON
    }

    // Extract summary field value from partial JSON
    // Handle both complete and partial JSON strings
    // Match: "summary": "..." with proper handling of escaped characters
    const summaryRegex = /"summary"\s*:\s*"((?:[^"\\]|\\.)*)"/;
    const summaryMatch = text_0.match(summaryRegex);
    if (summaryMatch && summaryMatch[1]) {
      // Unescape JSON string properly
      return summaryMatch[1].replace(/\\n/g, "\n").replace(/\\r/g, "\r").replace(/\\t/g, "\t").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
    }

    // Fallback: Try to find content between "summary": " and the next " or }
    // This handles cases where the JSON is still streaming
    const fallbackMatch = text_0.match(/"summary"\s*:\s*"([^"]*)/);
    if (fallbackMatch && fallbackMatch[1]) {
      return fallbackMatch[1].replace(/\\n/g, "\n").replace(/\\r/g, "\r").replace(/\\t/g, "\t").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
    }

    // Last resort: Remove JSON structure characters
    let cleaned = text_0.replace(/^\s*\{\s*"summary"\s*:\s*"/, "") // Remove opening JSON structure
    .replace(/"\s*,\s*"suggestions"\s*:.*$/, "") // Remove suggestions part
    .replace(/"\s*\}$/, "") // Remove closing brace
    .replace(/\\n/g, "\n") // Unescape newlines
    .replace(/\\"/g, '"') // Unescape quotes
    .replace(/\\\\/g, "\\"); // Unescape backslashes

    // Remove any remaining JSON structure characters at the start/end
    cleaned = cleaned.replace(/^[\s{"]+/, "").replace(/["}\s,]+$/, "");
    return cleaned;
  };

  // Fetch cached summary
  const {
    data: cachedSummary,
    isLoading: isLoadingCache,
    isFetched
  } = useQuery(aiSummaryQueryOptions.detail(patient.id));

  // Detect if the summary is stale (patient data has been updated since summary was generated)
  const isStale = (() => {
    if (!cachedSummary?.createdDate) return false;
    const latestDataDate = getLatestDataDate(patient, recentNotes, appointments, charges, formSubmissions, quickNotes, documents);
    if (!latestDataDate) return false;
    const summaryDate = new Date(cachedSummary.createdDate);
    return latestDataDate > summaryDate;
  })();

  // Build context for prompts (moved before generateSummary to avoid initialization error)
  const getEventTitle = useEventTitle();
  const context = useMemo(() => buildContextFromPatient(getEventTitle, patient, recentNotes, appointments, charges, forms, formSubmissions, quickNotes, documents), [getEventTitle, patient, recentNotes, appointments, charges, forms, formSubmissions, quickNotes, documents]);

  // AI generation state
  const [generatedText, setGeneratedText] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<{
    message: string;
    transient: boolean;
  } | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  // A dose edit or removal retires the cached summary. Drop the text this
  // session already rendered too, or it would keep showing the old dose.
  const cachedSummaryId = cachedSummary?.id;
  const previousCachedSummaryId = useRef(cachedSummaryId);
  useEffect(() => {
    if (previousCachedSummaryId.current && !cachedSummaryId) {
      setGeneratedText("");
    }
    previousCachedSummaryId.current = cachedSummaryId;
  }, [cachedSummaryId]);
  const latestContext = useRef(context);
  latestContext.current = context;

  // Generate summary using new backend endpoint
  const generateSummary = useCallback(async () => {
    setIsGenerating(true);
    setGenerationError(null);
    setGeneratedText("");
    const controller = new AbortController();
    setAbortController(controller);

    // Track summary generation start
    posthog.capture("patient_summary_generation_started", {
      patient_id: patient.id
    });
    try {
      let fullText = "";
      for await (const chunk of streamPatientSummary(context, controller.signal)) {
        if (controller.signal.aborted) {
          break;
        }
        if (typeof chunk === "string") {
          // Regular text chunk - append to accumulated text
          fullText += chunk;
          setGeneratedText(fullText);
        } else if (chunk.type === "validated") {
          // Backend validated citations - use the corrected version
          fullText = chunk.text;
          setGeneratedText(fullText);
        }
      }
      if (!controller.signal.aborted && fullText) {
        // Parse the response to extract summary and suggestions
        const {
          summary,
          suggestions
        } = parseAISummaryResponse(fullText);

        // Ensure we have a valid summary before saving
        if (summary && summary.trim().length > 0) {
          // Ensure we have exactly 4 suggestions
          const finalSuggestions = suggestions && suggestions.length >= 4 ? suggestions.slice(0, 4) : suggestions || [];

          // Persist only when the documents, appointments and notes behind it
          // are trustworthy, so a summary missing any of them isn't cached for
          // every other viewer. The summary still displays live this session.
          // If the chart changed mid-stream (a dose edit), drop the text so
          // the auto-generate effect rebuilds it from the current chart.
          if (latestContext.current !== context) {
            setGeneratedText("");
          } else if (canPersistSummary) {
            // Save to database (this handles both create and update)
            const savedSummary = await savePatientAISummary(patient.id, {
              summary,
              suggestions: finalSuggestions
            });

            // Update cache directly with the returned data to avoid extra GET request
            if (savedSummary) {
              queryClient.setQueryData(aiSummaryQueryKeys.detail(patient.id), savedSummary);
            }
          }

          // Track successful generation
          posthog.capture("patient_summary_generation_completed", {
            patient_id: patient.id,
            summary_length: summary.length,
            suggestions_count: finalSuggestions.length
          });
        }
        // Reset retry count on success
        setRetryCount(0);
      }
    } catch (error) {
      // User-initiated cancellations should not surface as errors.
      if (controller.signal.aborted || error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      // Surface the backend's user-facing message for known transient failures
      // (overload, rate limit, timeout, bad-output). Anything else falls back to
      // a generic message — we don't render raw exception text to providers.
      const isTransient = error instanceof SSEStreamError && error.code !== "fatal" && error.code !== "unknown";
      const friendlyMessage = error instanceof SSEStreamError ? error.userMessage : "Couldn't generate a summary. Please try again.";
      const technicalMessage = error instanceof Error ? error.message : "An error occurred";
      setGenerationError({
        message: friendlyMessage,
        transient: isTransient
      });

      // Track generation error (full technical message for debugging)
      posthog.capture("patient_summary_generation_error", {
        patient_id: patient.id,
        error_message: technicalMessage,
        error_code: error instanceof SSEStreamError ? error.code : undefined,
        retry_count: retryCount
      });

      // Only auto-retry on transient errors and within the retry budget.
      setRetryCount(prev => {
        if (isTransient && prev < MAX_RETRIES) {
          return prev + 1;
        }
        return 0;
      });
    } finally {
      setIsGenerating(false);
      setAbortController(null);
    }
  }, [context, canPersistSummary, queryClient, patient.id]);

  // Cancel generation
  const cancel = useCallback(() => {
    abortController?.abort();
    setIsGenerating(false);
    setGeneratedText("");
    setAbortController(null);
  }, [abortController]);

  // Delete mutation for regeneration
  const deleteMutation = useMutation({
    mutationFn: () => deletePatientAISummary(patient.id)
    // Don't invalidate queries here - we're about to generate a new summary anyway
  });

  // Feedback mutation
  const feedbackMutation = useMutation({
    mutationFn: ({
      feedbackType,
      feedbackText: feedbackText_0
    }: {
      feedbackType: AIFeedbackType;
      feedbackText?: string | null;
    }) => submitAISummaryFeedback(patient.id, {
      feedbackType,
      feedbackText: feedbackText_0 || null
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: aiSummaryQueryKeys.detail(patient.id)
      });
      setShowFeedbackDialog(false);
      setFeedbackText("");
      setPendingFeedbackType(null);
    }
  });

  // Handle feedback button click
  const handleFeedbackClick = useCallback((type: AIFeedbackType) => {
    // Track feedback click
    posthog.capture("patient_summary_feedback_clicked", {
      patient_id: patient.id,
      feedback_type: type === AIFeedbackType.POSITIVE ? "positive" : "negative"
    });
    if (type === AIFeedbackType.NEGATIVE) {
      // Show dialog for negative feedback to collect reason
      setPendingFeedbackType(type);
      setShowFeedbackDialog(true);
    } else {
      // Positive feedback can be submitted immediately
      feedbackMutation.mutate({
        feedbackType: type
      });
    }
  }, [feedbackMutation, patient.id]);

  // Handle feedback submission from dialog
  const handleSubmitFeedback = useCallback(() => {
    if (pendingFeedbackType) {
      feedbackMutation.mutate({
        feedbackType: pendingFeedbackType,
        feedbackText: feedbackText.trim() || null
      });
    }
  }, [pendingFeedbackType, feedbackText, feedbackMutation]);

  // Auto-retry on error (up to MAX_RETRIES)
  useEffect(() => {
    if (generationError && retryCount > 0 && retryCount <= MAX_RETRIES && !isGenerating) {
      // Wait a bit before retrying to avoid immediate retry loops
      const retryTimeout = setTimeout(() => {
        generateSummary();
      }, 1000);
      return () => clearTimeout(retryTimeout);
    }
  }, [generationError, retryCount, isGenerating, generateSummary]);

  // Auto-generate if no cached summary exists or if summary is stale
  useEffect(() => {
    if (isFetched && contextReady && !isGenerating && !generatedText && retryCount === 0 && !generationError) {
      // No cached summary: generate even if documents failed to load, so a
      // documents outage doesn't block the summary entirely (generateSummary
      // won't persist an untrustworthy result). If a cached summary already
      // exists, only regenerate when we can actually persist the improvement —
      // otherwise a documents outage would churn regeneration while the stale
      // cache stays the one shown.
      if (!cachedSummary) {
        generateSummary();
      } else if (isStale && canPersistSummary) {
        generateSummary();
      }
    }
  }, [isFetched, contextReady, canPersistSummary, cachedSummary, isGenerating, generatedText, isStale, generateSummary, retryCount, generationError]);

  // Get the summary and suggestions to display - parse from streaming response or cached
  const parsedResponse = generatedText ? parseAISummaryResponse(generatedText) : null;
  const displaySummary = cachedSummary?.summary || (() => {
    if (!parsedResponse) return undefined;
    // Filter out any format markers that might have leaked through
    return filterFormatMarkers(parsedResponse.summary);
  })();

  // Suggestions come pre-cleaned from parseAISummaryResponse (during streaming)
  // and from the API (which persists cleaned values).
  const displaySuggestions = parsedResponse?.suggestions && parsedResponse.suggestions.length > 0 ? parsedResponse.suggestions : cachedSummary?.suggestions;
  // Use cached date if available, otherwise use current time for newly generated summaries
  const lastUpdated = cachedSummary?.updatedDate || cachedSummary?.createdDate || (generatedText ? new Date().toISOString() : undefined);

  // Handle regeneration
  const handleRegenerate = useCallback(async () => {
    // Track regenerate click
    posthog.capture("patient_summary_regenerate_clicked", {
      patient_id: patient.id
    });
    setShowQA(false); // Collapse Q&A when regenerating
    setRetryCount(0); // Reset retry count when manually regenerating
    setGenerationError(null); // Clear any previous errors
    await deleteMutation.mutateAsync();
    generateSummary();
  }, [deleteMutation, generateSummary, patient.id]);

  // Handle copy to clipboard (strip citation markers for clean copy)
  const handleCopy = useCallback(async () => {
    if (displaySummary) {
      // Track copy click
      posthog.capture("patient_summary_copy_clicked", {
        patient_id: patient.id
      });

      // Remove citation markers for clipboard
      await navigator.clipboard.writeText(stripCitationRefs(displaySummary));
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    }
  }, [displaySummary, patient.id]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Track cancel click
    posthog.capture("patient_summary_cancel_clicked", {
      patient_id: patient.id
    });
    cancel();
  }, [cancel, patient.id]);

  // Handle citation click - navigate to the appropriate tab and highlight
  const handleCitationClick = useCallback((tab: string, appointmentId?: string) => {
    // Track citation/reference click
    posthog.capture("patient_summary_citation_clicked", {
      patient_id: patient.id,
      citation_tab: tab,
      appointment_id: appointmentId
    });
    const targetTab = TAB_MAP[tab] || tab;
    navigateToTab(patient.id, targetTab);

    // Highlight the relevant section after a small delay for tab switch
    setTimeout(() => {
      if (tab === "appointments" && appointmentId) {
        // Highlight specific appointment only
        const appointmentElement = document.getElementById(`appointment-${appointmentId}`);
        if (appointmentElement) {
          appointmentElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
          appointmentElement.classList.add("ai-citation-highlight");
          setTimeout(() => {
            appointmentElement.classList.remove("ai-citation-highlight");
          }, 2500);
        }
      } else if (tab !== "appointments") {
        // Only highlight sections for non-appointment tabs
        highlightSection(tab);
      }
      // For appointments without a specific ID, just navigate (no highlighting)
    }, 300);
  }, [patient.id, navigateToTab, highlightSection]);

  // Render text with clickable citations and proper list formatting
  const renderTextWithCitations = (text_1: string) => {
    return renderTextWithCitationsUtil(text_1, handleCitationClick);
  };

  // Handle expand/collapse toggle
  const handleExpandToggle = useCallback(() => {
    const newExpanded = !isExpanded;
    posthog.capture("patient_summary_expand_toggled", {
      patient_id: patient.id,
      expanded: newExpanded
    });
    setIsExpanded(newExpanded);
  }, [isExpanded, patient.id]);

  // Handle hover expand toggle (show more/less in collapsed view)
  const handleHoverExpandToggle = useCallback(() => {
    const newHoverExpanded = !hoverExpanded;
    posthog.capture("patient_summary_show_more_toggled", {
      patient_id: patient.id,
      expanded: newHoverExpanded
    });
    setHoverExpanded(newHoverExpanded);
  }, [hoverExpanded, patient.id]);

  // Handle Q&A toggle
  const handleQAToggle = useCallback(() => {
    if (isGenerating || isLoadingCache || !displaySummary || !contextReady) return;
    const newShowQA = !showQA;
    posthog.capture("patient_summary_qa_toggled", {
      patient_id: patient.id,
      qa_visible: newShowQA
    });
    setShowQA(newShowQA);
  }, [isGenerating, isLoadingCache, displaySummary, contextReady, showQA, patient.id]);
  const showMoreNeeded = displaySummary && displaySummary.split(/[.!?]+/).length > 3;
  const isRegenerateDisabled = isGenerating || deleteMutation.isPending || !contextReady;
  return <div className="mb-8 overflow-hidden rounded-lg border border-border bg-background transition-all">
      {/* Header with AI Summary label and actions */}
      <div className="flex items-center justify-between gap-2 border-b border-border bg-background-secondary/50 px-3 py-2">
        <div className="flex shrink-0 items-center gap-1.5">
          <AIBadge size={14} />
          <span className="whitespace-nowrap text-caption-strong text-primary">
            AI Summary
          </span>
        </div>

        <div className="flex items-center gap-0.5">
          {!isGenerating && displaySummary && <>
              {/* Feedback buttons */}
              <div className="mr-1 flex items-center gap-0.5 border-r border-border pr-1">
                <Tooltip content={cachedSummary?.feedbackType === AIFeedbackType.POSITIVE ? "Thanks for your feedback!" : "Good response"} triggerAsChild>
                  <Button type="button" variant="ghost" size="icon-sm" onClick={() => handleFeedbackClick(AIFeedbackType.POSITIVE)} disabled={feedbackMutation.isPending} className={cx(cachedSummary?.feedbackType === AIFeedbackType.POSITIVE ? "text-status-success-text" : "text-foreground-muted hover:text-status-success-text")}>
                    <FiThumbsUp className="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
                <Tooltip content={cachedSummary?.feedbackType === AIFeedbackType.NEGATIVE ? "Thanks for your feedback!" : "Needs improvement"} triggerAsChild>
                  <Button type="button" variant="ghost" size="icon-sm" onClick={() => handleFeedbackClick(AIFeedbackType.NEGATIVE)} disabled={feedbackMutation.isPending} className={cx(cachedSummary?.feedbackType === AIFeedbackType.NEGATIVE ? "text-status-error-text" : "text-foreground-muted hover:text-status-error-text")}>
                    <FiThumbsDown className="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
              </div>

              <Tooltip content={hasCopied ? "Copied!" : "Copy"} triggerAsChild>
                <Button type="button" variant="ghost" size="icon-sm" onClick={handleCopy} className={cx(hasCopied ? "text-status-success-text" : "text-foreground-secondary hover:text-foreground")}>
                  {hasCopied ? <FiCheck className="h-4 w-4" /> : <FiCopy className="h-4 w-4" />}
                </Button>
              </Tooltip>
              {/* Wrapped so the tooltip still shows while the button is disabled. */}
              <Tooltip content="Regenerate" triggerAsChild>
                <span className="inline-flex" tabIndex={isRegenerateDisabled ? 0 : undefined}>
                  <Button type="button" variant="ghost" size="icon-sm" onClick={handleRegenerate} disabled={isRegenerateDisabled} className="text-foreground-secondary hover:text-foreground disabled:opacity-50">
                    <FiRefreshCw className={cx("h-4 w-4", (isGenerating || deleteMutation.isPending) && "animate-spin")} />
                  </Button>
                </span>
              </Tooltip>
            </>}

          {isGenerating && <Tooltip content="Cancel" triggerAsChild>
              <Button type="button" variant="ghost" size="icon-sm" onClick={handleCancel} className="text-foreground-secondary hover:text-status-error-text">
                <FiX className="h-4 w-4" />
              </Button>
            </Tooltip>}

          {generationError && <Tooltip content="Retry" triggerAsChild>
              <Button type="button" variant="ghost" size="icon-sm" onClick={handleRegenerate} className="text-foreground-secondary hover:text-foreground">
                <FiRefreshCw className="h-4 w-4" />
              </Button>
            </Tooltip>}
        </div>
      </div>

      {/* Summary Content */}
      <div className="min-h-[60px] px-3 py-2">
        {isLoadingCache || !contextReady && !displaySummary ? <div className="space-y-2">
            <div className="h-4 w-full animate-pulse rounded bg-background-secondary" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-background-secondary" />
          </div> : generationError ? <div className="flex items-center justify-between gap-3">
            <span className="text-body text-foreground-secondary">
              {retryCount > 0 ? "Retrying…" : generationError.message}
            </span>
            {retryCount === 0 && <Button type="button" variant="ghost" size="sm" onClick={handleRegenerate} disabled={isGenerating || deleteMutation.isPending || !contextReady} className="shrink-0">
                <FiRefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Try again
              </Button>}
          </div> : isGenerating ? <div className="whitespace-pre-wrap text-body leading-relaxed text-foreground">
            {generatedText && renderTextWithCitations(extractSummaryFromPartialJSON(generatedText))}
            {generatedText && <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-primary" />}
          </div> : displaySummary ? <div>
            {isExpanded ? <div className="text-body leading-relaxed text-foreground">
                <p className="whitespace-pre-wrap">
                  {renderTextWithCitations(displaySummary)}
                </p>
                {showMoreNeeded && <button onClick={handleExpandToggle} className="mt-2 flex items-center gap-1 text-caption-strong text-primary hover:text-accent-700">
                    Show less <FiChevronUp className="h-3 w-3" />
                  </button>}
              </div> : <div>
                <div className={cx("relative overflow-hidden text-body leading-relaxed text-foreground transition-[max-height] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]", hoverExpanded ? "max-h-[1000px]" : "max-h-[3.5rem]")}>
                  {renderTextWithCitations(displaySummary)}
                  {/* Fade overlay at bottom when collapsed */}
                  <div className={cx("pointer-events-none absolute bottom-0 left-0 right-0 h-5 bg-gradient-to-t from-background via-background/90 to-transparent transition-opacity duration-300", hoverExpanded ? "opacity-0" : "opacity-100")} />
                </div>
                {/* Show more/less toggle */}
                {showMoreNeeded && <button onClick={handleHoverExpandToggle} className="mt-1 text-tiny text-foreground-muted transition-colors duration-200 hover:text-primary">
                    {hoverExpanded ? "Show less" : "Show more"}
                  </button>}
              </div>}
          </div> : <span className="text-body text-foreground-muted">
            No summary available. Click regenerate to create one.
          </span>}
      </div>

      {/* Q&A Toggle - directly attached to content */}
      <div onClick={handleQAToggle} className={cx("flex items-center gap-1.5 border-t border-border px-3 py-2 transition-colors", isGenerating || isLoadingCache || !displaySummary || !contextReady ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-hover")}>
        <span className="text-caption-strong text-primary">
          {showQA ? "Hide Q&A" : "Ask a question..."}
        </span>
      </div>

      {/* Q&A Interface */}
      {showQA && <div className={cx("border-t border-border transition-opacity", (isGenerating || isLoadingCache || !displaySummary || !contextReady) && "pointer-events-none opacity-50")}>
          <AIQAInterface context={context} patientId={patient.id} aiSuggestions={displaySuggestions} />
        </div>}

      {/* Generation Date/Time Footer */}
      {lastUpdated && <div className="border-t border-border bg-background-secondary/50 px-3 py-1.5">
          <span className="text-tiny leading-tight text-foreground-muted sm:text-caption">
            Generated {formatDateTime(lastUpdated)}.
          </span>
        </div>}

      {/* Feedback Dialog for Negative Feedback */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Why is this summary not helpful?</DialogTitle>
            <DialogDescription>
              Your feedback helps us improve the AI summary quality.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} placeholder="Please describe what's wrong or what could be improved..." className="min-h-[100px] w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-body text-foreground placeholder-foreground-muted transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30" autoFocus />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => {
            setShowFeedbackDialog(false);
            setFeedbackText("");
            setPendingFeedbackType(null);
          }} disabled={feedbackMutation.isPending}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmitFeedback} disabled={feedbackMutation.isPending}>
              {feedbackMutation.isPending ? "Submitting..." : "Submit Feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}